import StatsCalendar from "@/pages/settings/stats-calendar.svelte";
import StatsCharts from "@/pages/settings/stats-charts.svelte";
import StatsJournal from "@/pages/settings/stats-journal.svelte";
import StatsMap from "@/pages/settings/stats-map.svelte";
import Stats from "@/pages/settings/stats.svelte";
import { type Router } from "framework7/lite/bundle";
import { SETTINGS, getSetting, isFirstStart } from '../js/settings.ts';
import { logAndShowError } from './app.ts';
import { DEV_MODE } from './env.ts';

export const ROUTE_REPORT_ISSUE = '/report-issue/';
export const ROUTE_INTRODUCTION = '/introduction/';
export const ROUTE_SETTINGS = '/settings/';
export const ROUTE_SESSION = '/sunrise/';
export const ROUTE_SESSION_5M = `${ROUTE_SESSION}5`;
export const ROUTE_STATS = `/statistics/`;
export const ROUTE_STATS_CALENDAR = `${ROUTE_STATS}calendar/`;
export const ROUTE_STATS_MAP = `${ROUTE_STATS}map/`;
export const ROUTE_STATS_CHARTS = `${ROUTE_STATS}charts/`;
export const ROUTE_NOTIFICATIONS = `${ROUTE_SETTINGS}notifications/`;
export const ROUTE_TIMER = '/timer/';
export const ROUTE_DEBUG = '/debug/';
export const ROUTE_HOME = '/';
export const ROUTE_SESSION_SETTINGS = `${ROUTE_SETTINGS}session/`;
export const ROUTE_SETTINGS_EXERCISE = `${ROUTE_SETTINGS}exercise/`;
export const ROUTE_SETTINGS_ABOUT_CHANGES = `${ROUTE_SETTINGS}about/changes/`;
export const ROUTE_NEWS = '/news/';

let showIntroPage = isFirstStart();

/**
 * Reusable helper to dynamically load Svelte components for Framework7 routes.
 * * @param importFn - A function that returns a Promise resolving to a module with a default Svelte component.
 */
export const lazyLoad = (importFn: () => Promise<{ default: any }>) => { // eslint-disable-line
  return async ({ resolve, reject, to }: Router.RouteCallbackCtx): Promise<void> => {
    try {
      const module = await importFn();
      // Safely extract the default Svelte component export
      resolve({ component: module.default });
    } catch (e) {
      logAndShowError(`Failed to load route component for path ${to.path}`, e);
      reject();
    }
  };
};


// Config array containing metadata for each routable tab
export const StatsTabs = [
  { id: "tab-journal", href: '/', component: StatsJournal, text: "settings.my_journal", icon: "bar_chart", color: "grey" },
  { id: "tab-calendar", href: '/calendar/', component: StatsCalendar, text: "stats.calendar", icon: "event", color: "orange" },
  { id: "tab-map", href: '/map/', component: StatsMap, text: "stats.map", icon: "map", color: "blue" },
  { id: "tab-charts", href: '/charts/', component: StatsCharts, text: "stats.charts", icon: "pie_chart", color: "green" }
];

const routes: Router.RouteParameters[] = [
  {
    path: '/',
    async: lazyLoad(() => showIntroPage ? import('../pages/introduction.svelte') : import('../pages/home.svelte')),
    options: {
      animate: false,
      transition: 'f7-fade',
    },
  },
  {
    path: ROUTE_SESSION + ':duration', // duration of session in minutes
    async: lazyLoad(() => import('../pages/sunrise.svelte')),
    options: {
      transition: 'f7-fade',
    },
  },
  {
    path: ROUTE_INTRODUCTION,
    async: lazyLoad(() => import('../pages/introduction.svelte')),
    options: {
      animate: false,
    },
  },
  {
    path: ROUTE_SETTINGS,
    async: lazyLoad(() => import('../pages/settings.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_NOTIFICATIONS,
    async: lazyLoad(() => import('../pages/settings/notifications.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_SESSION_SETTINGS,
    async: lazyLoad(() => import('../pages/settings/session.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_SETTINGS_EXERCISE,
    async: lazyLoad(() => import('../pages/settings/exercise.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: `${ROUTE_SETTINGS}licenses/`,
    async: lazyLoad(() => import('../pages/settings/licenses.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: `${ROUTE_SETTINGS}about/`,
    async: lazyLoad(() => import('../pages/settings/about.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_SETTINGS_ABOUT_CHANGES,
    async: lazyLoad(() => import('../pages/settings/about-changes.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: `${ROUTE_SETTINGS}privacy/`,
    async: lazyLoad(() => import('../pages/settings/privacy.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_STATS,
    component: Stats,
    // routable tabs
    tabs: StatsTabs.map(tab => ({
      path: tab.href,
      id: tab.id,
      component: tab.component
    })),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_DEBUG,
    async: lazyLoad(() => import('../pages/debug.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_REPORT_ISSUE,
    async: lazyLoad(() => import('../pages/reportissue.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  },
  {
    path: ROUTE_NEWS,
    async: lazyLoad(() => import('../pages/news.svelte')),
    options: {
      transition: 'f7-parallax',
    },
  }
];

if (DEV_MODE) {
  routes.push(
    {
      path: '/story',
      async: lazyLoad(() => import('../components/story-list.svelte')),
      options: {
        transition: 'f7-parallax',
      },
    },
    {
      path: '/story/:title',
      async: lazyLoad(() => import('../components/story-editor.svelte')),
      options: {
        transition: 'f7-parallax',
      },
    },
  );
}

// 404 for pages not matching
routes.push(
  {
    path: '(.*)',
    async: lazyLoad(() => import('../pages/404.svelte')),
  }
);

export const navigateSession = (f7router: Router.Router, minutes?: number, props?: object) => {
  if (!minutes) { // use settings
    minutes = getSetting(SETTINGS.default_session_time) as number;
  }
  f7router.navigate(`${ROUTE_SESSION}${minutes}`, {
    history: false,
    browserHistory: false,
    ...props
  });
};

export const navigateHome = (f7router: Router.Router) => {
  f7router.navigate("/", {
    animate: true,
    clearPreviousHistory: true
  });
};

export const setShowIntroPage = (show: boolean) => {
  showIntroPage = show;
}

export default routes;

